// ─── Status machine ────────────────────────────────────────────────────────────
export type FilingStatus =
  // tax-compliance parse statuses
  | 'requested'
  | 'generation_completed'
  | 'generation_failed'
  | 'processing'
  | 'completed'
  | 'failed'
  // pipeline (opfin job execution) statuses
  | 'filing_data_generation_triggered'
  | 'filing_data_generation_started'
  | 'filing_data_generation_completed'
  | 'filing_data_generation_failed'
  | 'filing_data_generation_skipped'
  // downstream (FVU + TDS filing) statuses
  | 'fvu_generation_triggered'
  | 'fvu_generation_completed'
  | 'fvu_generation_failed'
  | 'tds_filing_triggered'
  | 'tds_filing_completed'
  | 'tds_filing_failed';

export type FilingType = '24Q' | '26Q' | '138' | '140';
export type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';
export type ErrorSource = 'filing_data_generation' | 'fvu_validation' | 'parse_validation';
export type ErrorType =
  | 'duplicate_pan'
  | 'invalid_data'
  | 'tds_shortfall'
  | 'tds_mismatch'
  | 'parse_error'
  | 'missing_field'
  | 'invalid_format'
  | 'fvu_rejection'
  | 'orphan_record'
  | 'count_mismatch'
  | 'invalid_pan'
  | 'invalid_tan';
export type RecordType = 'FH' | 'BH' | 'CD' | 'DD' | 'SD' | 'S16' | 'C6A';

// ─── Pagination ────────────────────────────────────────────────────────────────
export interface PaginatedResponse<T> {
  items: T[];
  total_count: number;
  page: number;
  page_size: number;
}

// ─── Filing list response ──────────────────────────────────────────────────────
export interface FilingDataListResponse extends PaginatedResponse<FilingDataListItem> {
  status_counts?: Partial<Record<FilingStatus, number>>;
}

// ─── Parse summary ─────────────────────────────────────────────────────────────
export interface ParseSummary {
  challan_count: number;
  deductee_count: number;
  salary_count: number;
  s16_count: number;
  c6a_count: number;
}

// ─── Filing list item ──────────────────────────────────────────────────────────
export interface FilingDataListItem {
  filing_data_id: string;
  organization_id: number | null;
  merchant_id: string | null;
  filing_type: FilingType;
  quarter: Quarter;
  fy: string;
  version: number;
  status: FilingStatus;
  status_updated_at: number;
  ufh_file_id: string | null;
  error_count: number;
  created_at: number;
  display_status: FilingStatus;
  pipeline_status: FilingStatus | null;
  job_source: string | null;
  downstream_status: FilingStatus | null;
  request_id: string | null;
}

// ─── Filing details (FH + BH fields) ──────────────────────────────────────────
export interface FilingDetails {
  filing_data_id: string;
  organization_id: number | null;
  merchant_id: string | null;
  filing_type: FilingType;
  quarter: Quarter;
  fy: string;
  version: number;
  status: FilingStatus;
  status_updated_at: number;
  ufh_file_id: string | null;
  ufh_file_name: string | null;
  file_hash_sha256: string | null;
  error_count: number;
  created_at: number;
  fh_file_type: string | null;
  fh_upload_type: string | null;
  fh_file_creation_date: string | null;
  fh_tan: string | null;
  fh_return_prep_utility: string | null;
  fh_line_number: number | null;
  bh_form_no: string | null;
  bh_pan_deductor: string | null;
  bh_assessment_year: string | null;
  bh_financial_year: string | null;
  bh_period: string | null;
  bh_employer_name: string | null;
  bh_employer_branch: string | null;
  bh_employer_address: string | null;
  bh_employer_state: string | null;
  bh_employer_pin: string | null;
  bh_employer_email: string | null;
  bh_deductor_type: string | null;
  bh_responsible_person_name: string | null;
  bh_responsible_person_designation: string | null;
  bh_responsible_person_address: string | null;
  bh_responsible_person_state: string | null;
  bh_responsible_person_pin: string | null;
  bh_responsible_person_email: string | null;
  bh_responsible_person_mobile: string | null;
  bh_responsible_person_pan: string | null;
  bh_batch_deposit_total: number | null;
  bh_challan_count: number | null;
  bh_deductee_count: number | null;
  bh_salary_record_count: number | null;
  bh_gross_total_income: number | null;
  bh_line_number: number | null;
  parse_summary: ParseSummary | null;
}

// ─── Challan (CD record) ──────────────────────────────────────────────────────
export interface Challan {
  challan_id: string;
  filing_data_id: string;
  line_number: number;
  challan_record_number: number;
  deductee_count: number;
  bank_challan_no: string | null;
  bsr_code: string | null;
  challan_date: number | null;
  tds_income_tax: number;
  tds_surcharge: number;
  tds_cess: number;
  tds_interest: number;
  tds_others: number;
  total_deposit_amount: number;
  tds_total_amount: number;
}

// ─── Deductee (DD record) ─────────────────────────────────────────────────────
export interface Deductee {
  deductee_id: string;
  filing_data_id: string;
  challan_id: string;
  line_number: number;
  deductee_record_number: number;
  pan: string;
  name: string;
  tds_amount: number;
  surcharge: number;
  cess: number;
  total_tax: number;
  total_deposit: number | null;
  payment_amount: number | null;
}

// ─── Section 16 deduction ─────────────────────────────────────────────────────
export interface S16Record {
  s16_id: string;
  salary_id: string;
  serial_no: number;
  section_code: string;
  amount: number;
}

// ─── Chapter VI-A deduction ───────────────────────────────────────────────────
export interface C6ARecord {
  c6a_id: string;
  salary_id: string;
  serial_no: number;
  section_id: string;
  gross_amount: number;
  qualifying_amount: number;
  deductible_amount: number;
}

// ─── Salary (SD record, Q4 only) ──────────────────────────────────────────────
export interface Salary {
  salary_id: string;
  filing_data_id: string;
  line_number: number;
  deductee_pan: string;
  employee_name: string;
  employee_category: string | null;
  employment_from: number | null;
  employment_to: number | null;
  total_salary: number | null;
  income_tax: number | null;
  surcharge: number | null;
  education_cess: number | null;
  net_tax_payable: number | null;
  total_tax_deducted: number | null;
  tax_shortfall: number | null;
  old_regime_indicator: string | null;
  section16_count: number | null;
  section16_total: number | null;
  chapter6a_count: number | null;
  chapter6a_total: number | null;
  net_taxable_income: number | null;
  gross_salary_17_1: number | null;
  perquisites_17_2: number | null;
  profits_17_3: number | null;
  s16: S16Record[];
  c6a: C6ARecord[];
}

// ─── Filing error ─────────────────────────────────────────────────────────────
export interface FilingError {
  error_id: string;
  filing_data_id: string;
  error_source: ErrorSource;
  error_type: ErrorType;
  fvu_error_code: string | null;
  fvu_error_message: string | null;
  error_details: Record<string, unknown>;
  people_id: number | null;
  line_number: number | null;
  record_type: RecordType | null;
  field_index: number | null;
  challan_id: string | null;
  deductee_id: string | null;
  salary_id: string | null;
  payroll_month: string | null;
  created_at: number;
}

// ─── Request params ───────────────────────────────────────────────────────────
export interface ListFilingDataParams {
  org_ids?: string;
  merchant_ids?: string;
  filing_type?: FilingType;
  quarter?: Quarter;
  fy?: string;
  statuses?: string;
  has_errors?: boolean;
  page?: number;
  page_size?: number;
}

export interface GenerateFilingDataRequest {
  org_ids: string[];
  quarter: Quarter;
  fy: string;
}

export interface GetChallansParams {
  page?: number;
  page_size?: number;
}

export interface GetDeducteesParams {
  challan_id?: string;
  page?: number;
  page_size?: number;
}

export interface GetSalariesParams {
  page?: number;
  page_size?: number;
}

export interface GetErrorsParams {
  error_source?: ErrorSource;
  error_type?: ErrorType;
  record_type?: RecordType;
  page?: number;
  page_size?: number;
}

export interface GenerateFilingDataResponse {
  id: string;
  status: FilingStatus;
  version: number;
}

export interface DownloadUrlResponse {
  signed_url: string;
  expires_at: number;
  file_type: string;
}

export const TDS_FILING_DATA_QUERY_KEYS = {
  list: 'tds-filing-data-list',
  statusCounts: 'tds-filing-status-counts',
  details: (id: string) => ['tds-filing-data-details', id],
  challans: (id: string) => ['tds-filing-challans', id],
  deductees: (id: string, challanId?: string) => ['tds-filing-deductees', id, challanId],
  salaries: (id: string) => ['tds-filing-salaries', id],
  errors: (id: string) => ['tds-filing-errors', id],
} as const;
